import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions, breadcrumbBadge, icon, emoji, compact = false }) {
  const headerIcon = icon ?? emoji;

  return (
    <div className={cn("flex flex-wrap items-end justify-between gap-2 min-w-0", compact ? "mb-3" : "mb-5")}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 min-w-0">
          {headerIcon && (
            <span
              className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-stone-50 to-brand-violet-soft/40 border border-stone-200 text-brand-primary shadow-brand-sm shrink-0"
              aria-hidden
            >
              {headerIcon}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1
                className={cn(
                  "font-bold tracking-tight text-stone-900 truncate",
                  compact ? "text-[18px] sm:text-[20px]" : "text-[20px] sm:text-[24px]",
                )}
                data-testid="page-title"
              >
                {title}
              </h1>
              {breadcrumbBadge}
            </div>
            {subtitle && (
              <p className="text-[12.5px] sm:text-[13.5px] text-stone-500 mt-0.5 line-clamp-2 sm:line-clamp-none">
                {subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-2 max-w-full overflow-x-auto pb-0.5 shrink-0">{actions}</div>
      )}
    </div>
  );
}

export function PageContainer({ children, className }) {
  return (
    <div
      className={cn("px-3 py-3 sm:px-4 sm:py-4 md:px-5 md:py-5 w-full max-w-none min-w-0 animate-fade-up", className)}
      data-testid="page-container"
    >
      {children}
    </div>
  );
}

export function Card({ children, className, padded = true, hover = false }) {
  return (
    <div
      className={cn(
        "bg-white backdrop-blur-md border border-stone-200/80 rounded-2xl shadow-brand min-w-0",
        hover &&
          "transition-all duration-200 hover:-translate-y-0.5 hover:shadow-brand-lift hover:border-stone-300/80",
        padded && "p-4 sm:p-5",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children, action, className, icon, emoji }) {
  const sectionIcon = icon ?? emoji;

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-2 mb-3 min-w-0", className)}>
      <h2 className="text-[14.5px] font-semibold tracking-tight text-stone-900 flex items-center gap-1.5">
        {sectionIcon && <span aria-hidden className="text-brand-primary">{sectionIcon}</span>}
        {children}
      </h2>
      {action}
    </div>
  );
}

export function Pill({ tone = "default", children, className }) {
  const tones = {
    default: "bg-stone-100 text-stone-700 border-stone-200",
    blue: "bg-stone-100 text-stone-800 border-stone-200",
    green: "bg-stone-100 text-brand-primary-dark border-brand-surface-border",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-rose-50 text-rose-700 border-rose-200",
    purple: "bg-brand-violet-soft/50 text-brand-primary-dark border-brand-surface-border",
    teal: "bg-stone-100 text-brand-primary-dark border-brand-surface-border",
    rose: "bg-brand-violet-soft/50 text-brand-primary-dark border-brand-surface-border",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2 h-5 rounded-md text-[11px] font-medium border whitespace-nowrap",
        tones[tone] || tones.default,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function StatusDot({ tone = "default", className }) {
  const colors = {
    default: "bg-stone-400",
    green: "bg-brand-primary",
    red: "bg-rose-500",
    amber: "bg-amber-500",
    blue: "bg-brand-gold",
  };
  return <span className={cn("inline-block h-1.5 w-1.5 rounded-full", colors[tone], className)} />;
}

export function EmptyHint({ children }) {
  return (
    <div className="text-center text-[12.5px] text-stone-400 py-10 border border-dashed border-stone-200 rounded-2xl bg-stone-50/50">
      {children}
    </div>
  );
}
